import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import {
  Plus,
  Trash,
  PencilSimple,
  X,
  Eye,
  EyeSlash,
  CursorClick,
} from "@phosphor-icons/react";
import { formatNumber } from "@/lib/utils-format";
import Modal from "@/components/Modal";
import MediaUpload from "@/components/MediaUpload";

const COVER_PLACEHOLDER =
  "https://static.prod-images.emergentagent.com/jobs/fa232cd9-0eae-4d04-82d8-ba6f3508d2ae/images/c0fe1f59cfc238b37edb56856d8b710318e2a5843ec82c00ece85ea189f80ec4.png";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Programs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/programs");
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить программу из каталога?")) return;
    await api.delete(`/programs/${id}`);
    load();
  };

  const toggle = async (p) => {
    await api.put(`/programs/${p.id}`, { ...p, is_active: !p.is_active });
    load();
  };

  return (
    <div data-testid="programs-page">
      <PageHeader
        title="Каталог программ"
        subtitle="Курсы, которые видят пользователи в боте при нажатии на «Каталог программ»"
        actions={
          <button
            data-testid="create-program-btn"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
          >
            <Plus size={14} weight="bold" /> Новая программа
          </button>
        }
      />

      {loading ? (
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      ) : items.length === 0 ? (
        <div className="bg-zinc-900/40 border border-dashed border-zinc-800 rounded-lg p-12 text-center">
          <div className="text-zinc-300 font-semibold mb-1">
            Каталог пока пуст
          </div>
          <div className="text-sm text-zinc-500 mb-4">
            Создайте первую программу — она появится в боте у всех подписчиков.
          </div>
          <button
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium px-3 py-2 rounded-md"
          >
            <Plus size={14} weight="bold" /> Создать программу
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <div
              key={p.id}
              data-testid={`program-card-${p.id}`}
              className="bg-zinc-900/60 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-700 transition-colors"
            >
              <div
                className="h-32 bg-cover bg-center relative"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(9,9,11,0.95)), url(${
                    p.cover_url
                      ? p.cover_url.startsWith("/")
                        ? BACKEND_URL + p.cover_url
                        : p.cover_url
                      : COVER_PLACEHOLDER
                  })`,
                }}
              >
                <div className="absolute top-2 right-2">
                  <div
                    className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide rounded-full border ${
                      p.is_active
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                        : "bg-zinc-800/80 text-zinc-400 border-zinc-700"
                    }`}
                  >
                    {p.is_active ? "активна" : "скрыта"}
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold leading-tight truncate">
                  {p.name}
                </h3>
                {p.short_description && (
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">
                    {p.short_description}
                  </p>
                )}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <Stat label="Длит." value={p.duration || "—"} mono />
                  <Stat
                    label="Клики"
                    value={formatNumber(p.clicks || 0)}
                    icon={CursorClick}
                  />
                  <Stat
                    label="Сравн."
                    value={formatNumber(p.compares || 0)}
                  />
                </div>
                {p.tariffs?.length > 0 && (
                  <div className="mt-3 text-xs text-zinc-400">
                    {p.tariffs.map((t, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{t.name}</span>
                        <span className="font-mono">
                          {t.price} {t.currency || ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 mt-4 pt-3 border-t border-zinc-800">
                  <button
                    onClick={() => toggle(p)}
                    data-testid={`toggle-program-${p.id}`}
                    className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-zinc-800 hover:border-zinc-700"
                  >
                    {p.is_active ? (
                      <>
                        <EyeSlash size={12} /> Скрыть
                      </>
                    ) : (
                      <>
                        <Eye size={12} /> Показать
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => setEditing(p)}
                    data-testid={`edit-program-${p.id}`}
                    className="flex items-center gap-1 text-xs px-2 py-1.5 rounded-md border border-zinc-800 hover:border-blue-500/50 hover:text-blue-400"
                  >
                    <PencilSimple size={12} /> Изменить
                  </button>
                  <button
                    onClick={() => remove(p.id)}
                    data-testid={`delete-program-${p.id}`}
                    className="ml-auto text-zinc-500 hover:text-red-400 p-1.5"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProgramModal
          program={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, mono, icon: Icon }) {
  return (
    <div className="bg-zinc-950/60 rounded-md px-2 py-1.5 border border-zinc-900">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
        {Icon && <Icon size={9} weight="bold" />}
        {label}
      </div>
      <div className={`text-[12px] truncate ${mono ? "font-mono" : ""}`}>
        {value}
      </div>
    </div>
  );
}

function ProgramModal({ program, onClose, onSaved }) {
  const isNew = !program.id;
  const [form, setForm] = useState({
    name: program.name || "",
    short_description: program.short_description || "",
    description: program.description || "",
    results: program.results || "",
    duration: program.duration || "",
    deadline: program.deadline || "",
    landing_url: program.landing_url || "",
    cover_file_id: program.cover_file_id || "",
    cover_url: program.cover_url || "",
    images: program.images || [],
    product_key: program.product_key || "",
    tariffs: program.tariffs || [],
    is_active: program.is_active === undefined ? true : !!program.is_active,
    position: program.position || 0,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [uploadKey, setUploadKey] = useState(0);

  const addImage = (url) => {
    if (!url) return;
    setForm((f) => ({ ...f, images: [...f.images, url] }));
    setUploadKey((k) => k + 1);
  };

  const removeImage = (i) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }));
  };

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      if (isNew) await api.post("/programs", form);
      else await api.put(`/programs/${program.id}`, form);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow={isNew ? "Новая программа" : "Редактирование"}
      title={isNew ? "Новая программа" : program.name}
      testid="program-modal"
      maxWidth="2xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md border border-zinc-800 hover:border-zinc-700"
          >
            Отмена
          </button>
          <button
            data-testid="save-program-btn"
            disabled={saving || !form.name.trim()}
            onClick={save}
            className="px-3 py-2 text-sm rounded-md bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-medium"
          >
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        <Field label="Название">
          <input
            data-testid="program-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Краткое описание (для каталога)">
          <input
            data-testid="program-short-input"
            value={form.short_description}
            onChange={(e) =>
              setForm({ ...form, short_description: e.target.value })
            }
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <Field label="Полное описание (HTML, показывается в боте)">
          <textarea
            data-testid="program-desc-textarea"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
          />
        </Field>
        <Field label="Результаты программы">
          <textarea
            data-testid="program-results-textarea"
            value={form.results}
            onChange={(e) => setForm({ ...form, results: e.target.value })}
            rows={3}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Длительность">
            <input
              data-testid="program-duration-input"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              placeholder="8 недель"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Дедлайн / Старт">
            <input
              data-testid="program-deadline-input"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              placeholder="01.02.2026"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ссылка на лендинг">
            <input
              data-testid="program-url-input"
              value={form.landing_url}
              onChange={(e) =>
                setForm({ ...form, landing_url: e.target.value })
              }
              placeholder="https://stratoplan-school.com/..."
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>
          <Field label="Позиция">
            <input
              type="number"
              data-testid="program-position-input"
              value={form.position}
              onChange={(e) =>
                setForm({ ...form, position: parseInt(e.target.value || "0") })
              }
              className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
            />
          </Field>
        </div>

        <Field label='Product-ключ для вебхука (поле "product" в Zapier)'>
          <input
            data-testid="program-product-key-input"
            value={form.product_key}
            onChange={(e) => setForm({ ...form, product_key: e.target.value })}
            placeholder="например: pm_intensive или course_practice"
            className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
          />
        </Field>

        <Field label="Обложка программы (превью в каталоге)">
          <MediaUpload
            testid="program-cover-upload"
            accept="image"
            value={form.cover_url}
            onChange={(url) => setForm({ ...form, cover_url: url })}
          />
        </Field>

        <Field label="Галерея изображений (слайды описания в боте)">
          {form.images.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {form.images.map((url, i) => (
                <div key={i} className="relative group">
                  <img
                    src={url.startsWith("/") ? BACKEND_URL + url : url}
                    alt={`slide-${i + 1}`}
                    className="w-20 h-16 object-cover rounded border border-zinc-800"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    data-testid={`remove-image-${i}`}
                    className="absolute top-0.5 right-0.5 bg-zinc-950/80 hover:bg-red-500 rounded p-0.5 text-zinc-200 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={10} weight="bold" />
                  </button>
                  <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] bg-zinc-950/70 text-zinc-400 rounded-b py-0.5">
                    {i + 1}
                  </span>
                </div>
              ))}
            </div>
          )}
          <MediaUpload
            key={uploadKey}
            testid="program-gallery-upload"
            accept="image"
            value=""
            onChange={addImage}
            helpText="PNG / JPG / WebP до 8 MB. Слайд добавляется к галерее, не заменяет предыдущий."
          />
        </Field>

        <Field label="Тарифы">
          <div className="space-y-1.5">
            {form.tariffs.map((t, i) => (
              <div key={i} className="flex gap-1.5 items-center">
                <input
                  value={t.name || ""}
                  onChange={(e) => {
                    const arr = [...form.tariffs];
                    arr[i] = { ...arr[i], name: e.target.value };
                    setForm({ ...form, tariffs: arr });
                  }}
                  placeholder="Базовый"
                  className="flex-1 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm"
                />
                <input
                  value={t.price || ""}
                  onChange={(e) => {
                    const arr = [...form.tariffs];
                    arr[i] = { ...arr[i], price: e.target.value };
                    setForm({ ...form, tariffs: arr });
                  }}
                  placeholder="49 000"
                  className="w-32 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
                />
                <input
                  value={t.currency || ""}
                  onChange={(e) => {
                    const arr = [...form.tariffs];
                    arr[i] = { ...arr[i], currency: e.target.value };
                    setForm({ ...form, tariffs: arr });
                  }}
                  placeholder="₽"
                  className="w-16 bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono"
                />
                <button
                  onClick={() =>
                    setForm({
                      ...form,
                      tariffs: form.tariffs.filter((_, j) => j !== i),
                    })
                  }
                  className="text-zinc-500 hover:text-red-400 p-1.5"
                >
                  <Trash size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setForm({
                  ...form,
                  tariffs: [
                    ...form.tariffs,
                    { name: "", price: "", currency: "₽" },
                  ],
                })
              }
              className="text-xs text-zinc-400 hover:text-blue-400 inline-flex items-center gap-1"
            >
              <Plus size={12} /> Добавить тариф
            </button>
          </div>
        </Field>

        <div className="flex items-center gap-6">
          <Switch
            testid="program-is-active"
            label="Видна в боте"
            value={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
          />
        </div>

        {err && (
          <div className="text-sm text-red-400" data-testid="program-error">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}

function Switch({ label, value, onChange, testid }) {
  return (
    <label
      data-testid={testid}
      className="flex items-center gap-2 cursor-pointer text-sm select-none"
    >
      <span
        className={`w-9 h-5 rounded-full relative transition-colors ${
          value ? "bg-blue-500" : "bg-zinc-800"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-zinc-50 transition-transform ${
            value ? "translate-x-4" : ""
          }`}
        />
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      {label}
    </label>
  );
}
